import {
  type AcpSessionConfigOption,
  type AgentConnectionStatus,
  agentModeChoices,
  type ConversationSettings,
  type CrowdexAgentInteraction,
  type CrowdexCollaboration,
  type CrowdexReferenceKind,
  type CrowdexUIMessage,
  crowdexReferenceKindSchema,
  isCrowdexInternalToolName,
  modelDisplayName,
} from '@crowdex/agent-contracts';
import type { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pluginContextReminder } from '@/features/apps/plugin-context';
import { usePluginWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useAgentPluginSkills } from '@/hooks/use-agent-plugin-skills';
import { useAiAgentCapabilities } from '@/hooks/use-ai-agent-capabilities';
import { useAiAgentSelection, useConversationSettings } from '@/hooks/use-conversation-settings';
import { useLocalWorkspaceServerStatus } from '@/hooks/use-local-workspace-server-status';
import {
  CrowdexAgentClient,
  type CrowdexAgentSessionOptions,
  createUnavailableCrowdexChatTransport,
} from '@/lib/agent-runtime/agent-client';
import {
  agentModelsKey,
  readAgentModels,
  saveAgentModels,
} from '@/lib/agent-runtime/agent-model-cache';
import {
  type AgentModelPreference,
  fetchAgentModelPreference,
  saveAgentModelPreference,
  saveConversationModel,
} from '@/lib/agent-runtime/agent-model-preference';
import { mergeSystemReminders } from '@/lib/agent-runtime/agent-reminders';
import {
  MESSAGE_PREDICTION_PROMPT,
  predictionFromOutput,
} from '@/lib/agent-runtime/message-prediction';
import { resolvedAgentModelId, savedAgentParticipant } from '@/lib/agent-runtime/saved-agents';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import { readAgentPersonas } from '@/lib/conversations/agent-personas';
import { personaSharesRuntime } from '@/lib/conversations/persona-runtime';
import {
  type AcpSelectConfigOption,
  concreteModelChoices,
  MODEL_LIST_RETRY,
  type ModelListState,
  matchModelChoice,
  modelListMeta,
  nativeAgentConfigOptions,
  selectModel,
} from '@/lib/desktop-config-options';
import { isLocalMachine, type LocalWorkspaceServer } from '@/lib/local-runtime';
import type { AgentStartupPhase } from '@/lib/native-agent/types';
import type { NativeAgent } from '@/lib/native-desktop';

const PERMISSION_CONFIG_ID = 'crowdex-permission-mode';
/** How long a turn waits for the agent's model list before asking the user to choose. */
const MODEL_LIST_WAIT_MS = 30_000;
/** When a session that failed to start is started again, after each failure. */
const PREPARATION_RETRIES_MS = [5_000, 20_000, 60_000] as const;
/** How long the page waits for the prepared session to say its models before saying it failed. */
const MODEL_LIST_TIMEOUT_MS = 45_000;

/** The user's permissions, capped by the selected agent profile's policy (as the server does). */
function runtimePermissionMode(
  userPolicy: 'allow-reads' | 'allow-edits' | 'allow-all',
  agentPolicy?: 'allow-reads' | 'allow-edits' | 'allow-all',
): 'allow-reads' | 'allow-edits' | 'allow-all' {
  const policies = ['allow-reads', 'allow-edits', 'allow-all'] as const;
  return (
    policies[
      Math.min(policies.indexOf(userPolicy), policies.indexOf(agentPolicy ?? 'allow-all'))
    ] ?? 'allow-reads'
  );
}

/** The model option's `description` and `_meta` for a list with nothing to choose from. */
function modelListStatus(state: ModelListState): {
  description: string;
  _meta: Record<string, unknown>;
} {
  return {
    description: state.state === 'loading' ? (state.message ?? 'Loading models…') : state.message,
    _meta: modelListMeta(state),
  };
}

/**
 * What a conversation's turns run with, as it was when it was last shown: a turn prepared in one
 * conversation (its options can take a while) never takes another's model, permissions, title or
 * plugin context.
 */
type ConversationTurnContext = {
  /** Why no turn can start yet, or `null`. */
  unavailable: string | null;
  settings: Pick<ConversationSettings, 'agentProfileId' | 'sessionConfigValues' | 'permissionMode'>;
  modelId: string;
  /** The agent's models are still loading and none is chosen: a turn waits for them. */
  modelLoading: boolean;
  initialSessionContext: string | null | undefined;
  persistence: {
    title: string;
    collaboration: CrowdexCollaboration;
    agentName: string;
    workspace: { name: string; runtime: 'local' | 'worktree' } | null;
  };
};

/**
 * One conversation's connection to its agent: the transport its chat sends through, and what
 * answers the turn running on it (approvals, interactions, stop). A turn is answered through the
 * connection it runs on, whichever conversation is shown.
 */
export type ConversationConnection = {
  conversationId: string;
  transport: DefaultChatTransport<CrowdexUIMessage>;
  executeClientTool: (name: string, input: Record<string, unknown>) => Promise<unknown>;
  respondToInteraction: (interaction: CrowdexAgentInteraction) => Promise<void>;
  cancelGeneration: () => Promise<void>;
  /** The user's likely next message after the latest reply, or null when there is none. */
  predictNextMessage: (abortSignal: AbortSignal) => Promise<string | null>;
};

/** Where a conversation that is not shown runs its turns (`connectionFor`). */
export type ConversationRunPlace = {
  agent: NativeAgent;
  workspaceRoot: string;
  cloud: boolean;
  /** A local conversation's folder name and kind, recorded with its turns. */
  persistenceWorkspace: { name: string; runtime: 'local' | 'worktree' } | null;
};

/** What of a conversation's turn context its session is made of. */
type TurnSessionContext = Pick<
  ConversationTurnContext,
  'unavailable' | 'settings' | 'modelId' | 'initialSessionContext'
>;

/** A conversation's session options, from its turn context. */
function turnSessionOptions(
  conversationId: string,
  context: TurnSessionContext,
): CrowdexAgentSessionOptions {
  if (context.unavailable !== null) throw new Error(context.unavailable);
  return {
    settings: { ...context.settings, modelId: context.modelId || null },
    // What only this page knows: a handoff's context, the plugin the user works with in this
    // conversation (its page, panel or artifact), on every turn.
    pageReminder: () =>
      mergeSystemReminders(context.initialSessionContext, pluginContextReminder(conversationId)),
  };
}

/** The session options of a generation that only reads. */
function readOnly(options: CrowdexAgentSessionOptions): CrowdexAgentSessionOptions {
  return { ...options, settings: { ...options.settings, permissionMode: 'allow-reads' } };
}

/**
 * Lightweight UI state for the server-owned AI SDK agent runtime. A turn sends its message and
 * the conversation's settings; the server and the CLI equip it, as any turn of the conversation.
 */
export function useAgentRuntime(options: {
  conversationId: string;
  agent: NativeAgent | null;
  workspaceRoot: string | null;
  logicalWorkspaceId?: string;
  cloudRuntime?: CloudRuntime | null;
  serverPersisted?: boolean;
  restoredCollaboration?: CrowdexCollaboration | null;
  preferredModelId?: string | null;
  initialSessionContext?: string | null;
  persistenceTitle?: string | null;
  /** A local conversation's folder name and kind, recorded with its turns. */
  persistenceWorkspace?: { name: string; runtime: 'local' | 'worktree' } | null;
  resolveReference?: (kind: CrowdexReferenceKind, id: string) => Promise<unknown>;
}) {
  const cloud = options.cloudRuntime !== null && options.cloudRuntime !== undefined;
  const { profile: selectedAiAgent } = useAiAgentSelection(options.conversationId);
  const conversationSettings = useConversationSettings(options.conversationId);
  // A group is one by nature, from its start: the collaboration never changes.
  const collaboration = options.restoredCollaboration ?? 'solo';
  const aiAgent = selectedAiAgent;
  const aiCapabilities = useAiAgentCapabilities(aiAgent, options.workspaceRoot, cloud);
  const aiAgentError =
    aiCapabilities.error ??
    (aiAgent && options.agent && !savedAgentParticipant(aiAgent, [options.agent], 'current')
      ? 'Select the ACP client required by this AI agent.'
      : null);
  const localServer = useLocalWorkspaceServerStatus();
  const pluginSkills = useAgentPluginSkills({
    enabled: !cloud && isLocalMachine(),
    workspaceRoot: options.workspaceRoot,
  });
  const pluginWorkbench = usePluginWorkbench(options.workspaceRoot);
  const preferredModelId = resolvedAgentModelId(aiAgent, options.preferredModelId ?? '');
  const activeAgentId = options.agent?.id ?? null;
  // A model picked for one agent is not another's: switching agents drops it (Codex's GPT-6.1-Sol
  // is no OpenCode model, and would show as its only one while OpenCode's list loads).
  const [modelState, setModelState] = useState({
    conversationId: options.conversationId,
    agentId: activeAgentId,
    preferredModelId,
    value: preferredModelId,
  });
  const modelId =
    modelState.conversationId === options.conversationId &&
    modelState.agentId === activeAgentId &&
    modelState.preferredModelId === preferredModelId
      ? modelState.value
      : preferredModelId;
  const setModelId = useCallback(
    (value: string) =>
      setModelState({
        conversationId: options.conversationId,
        agentId: activeAgentId,
        preferredModelId,
        value,
      }),
    [activeAgentId, options.conversationId, preferredModelId],
  );
  const [reportedConfigState, setReportedConfigState] = useState<{
    agentId: string | null;
    options: readonly AcpSessionConfigOption[];
  }>({ agentId: activeAgentId, options: [] });
  const reportedConfigOptions =
    reportedConfigState.agentId === activeAgentId ? reportedConfigState.options : [];
  const setReportedConfigOptions = useCallback(
    (configOptions: readonly AcpSessionConfigOption[]) =>
      setReportedConfigState({ agentId: activeAgentId, options: configOptions }),
    [activeAgentId],
  );
  // The model the latest reply ran, which names the default when nothing said it before.
  const [replyModelState, setReplyModelState] = useState<{
    conversationId: string;
    modelId: string | null;
  }>({ conversationId: options.conversationId, modelId: null });
  const replyModelId =
    replyModelState.conversationId === options.conversationId ? replyModelState.modelId : null;
  const setReplyModelId = useCallback(
    (modelId: string | null) =>
      setReplyModelState((current) =>
        current.conversationId === options.conversationId && current.modelId === modelId
          ? current
          : { conversationId: options.conversationId, modelId },
      ),
    [options.conversationId],
  );
  // What the session readied before the first message said of its models: a list (maybe empty),
  // or that it could not say them (a failure, a timeout, a CLI from before it said them).
  const [modelListAnswer, setModelListAnswer] = useState<{
    conversationId: string;
    failure: string | null;
  } | null>(null);
  const [modelListAttempt, setModelListAttempt] = useState(0);
  // The agent's first install, while the session readied before the first message waits on it.
  const [installingConversation, setInstallingConversation] = useState<string | null>(null);
  const reportedModelOption = reportedConfigOptions.find(
    (option): option is Extract<AcpSessionConfigOption, { type: 'select' }> =>
      option.type === 'select' && option.category === 'model',
  );
  // What this agent said of its models last time (shown at once, while its session starts).
  const modelsKey = options.agent === null ? null : agentModelsKey(options.agent);
  const cachedModels = useMemo(
    () => (modelsKey === null ? {} : readAgentModels(modelsKey)),
    [modelsKey],
  );
  // The model the user runs this agent on: an account preference, the same on every device.
  const preferenceAgentId = options.agent?.id ?? null;
  const [preference, setPreference] = useState<{
    agentId: string;
    value: AgentModelPreference;
  } | null>(null);
  const agentPreference =
    preference !== null && preference.agentId === preferenceAgentId ? preference.value : null;
  useEffect(() => {
    if (preferenceAgentId === null) return;
    let current = true;
    void fetchAgentModelPreference(preferenceAgentId).then((value) => {
      if (current && value !== null) setPreference({ agentId: preferenceAgentId, value });
    });
    return () => {
      current = false;
    };
  }, [preferenceAgentId]);
  // Else the models the agent last listed on the account (a desktop's, a cloud turn's): a cloud
  // session says none before its first answer, and a browser has no cache of its own.
  const accountModels = useMemo(
    () => accountModelOption(agentPreference?.listed ?? []),
    [agentPreference?.listed],
  );
  const modelChoices = concreteModelChoices(
    reportedModelOption ?? cachedModels.model ?? accountModels,
  );
  // The list the agent says is the account's too: a turn the server starts picks from it.
  const listedModels =
    reportedModelOption === undefined
      ? ''
      : JSON.stringify(modelChoices.map((choice) => choice.value));
  useEffect(() => {
    if (modelsKey === null || reportedModelOption === undefined) return;
    saveAgentModels(modelsKey, { model: reportedModelOption });
  }, [modelsKey, reportedModelOption]);
  useEffect(() => {
    if (preferenceAgentId === null || listedModels === '' || agentPreference === null) return;
    if (JSON.stringify(agentPreference.listed) === listedModels) return;
    const listed = JSON.parse(listedModels) as string[];
    setPreference({ agentId: preferenceAgentId, value: { ...agentPreference, listed } });
    void saveAgentModelPreference(preferenceAgentId, { listed });
  }, [agentPreference, listedModels, preferenceAgentId]);
  // Always a concrete model, forced on the session: never the agent's configuration.
  const selectedModelId = selectModel(
    [modelId, replyModelId, agentPreference?.model],
    modelChoices,
  );
  const sessionConfigValues = conversationSettings.sessionConfigValues;
  const chooseSettings = conversationSettings.choose;
  const referenceResolverRef = useRef(options.resolveReference);
  referenceResolverRef.current = options.resolveReference;
  // Each conversation's turn context (see `ConversationTurnContext`), the shown one's written as
  // it renders, and the conversation shown.
  const turnContextsRef = useRef(new Map<string, ConversationTurnContext>());
  const shownConversationRef = useRef(options.conversationId);
  shownConversationRef.current = options.conversationId;

  // The shown conversation's session, as its turn context (below) records it.
  const unavailable =
    aiAgentError ?? (aiCapabilities.loading ? 'AI agent capabilities are still loading.' : null);
  const shownSession = useMemo(
    (): TurnSessionContext => ({
      unavailable,
      settings: {
        agentProfileId: conversationSettings.agentProfileId,
        sessionConfigValues,
        permissionMode: conversationSettings.permissionMode,
      },
      modelId: selectedModelId,
      initialSessionContext: options.initialSessionContext,
    }),
    [
      unavailable,
      conversationSettings.agentProfileId,
      sessionConfigValues,
      conversationSettings.permissionMode,
      selectedModelId,
      options.initialSessionContext,
    ],
  );
  // A new identity when what it reads changes: the session is prepared again.
  const currentSessionOptions = useCallback(
    (): CrowdexAgentSessionOptions => turnSessionOptions(options.conversationId, shownSession),
    [options.conversationId, shownSession],
  );

  const ready =
    !aiCapabilities.loading &&
    !aiAgentError &&
    options.agent !== null &&
    pluginWorkbench.loaded &&
    conversationSettings.loaded &&
    options.workspaceRoot !== null &&
    (cloud || localServer?.state === 'ready');
  const error =
    aiAgentError ?? (!cloud && localServer?.state === 'failed' ? localServer.message : null);
  const status: AgentConnectionStatus = ready
    ? { state: 'ready' }
    : error === null
      ? { state: options.agent === null || options.workspaceRoot === null ? 'idle' : 'starting' }
      : { state: 'error', message: error };
  const startupPhase: AgentStartupPhase = ready
    ? 'ready'
    : error === null
      ? options.agent === null || options.workspaceRoot === null
        ? 'idle'
        : 'connecting'
      : 'error';

  // One client per agent, folder and server, shown conversation or not: a turn is followed (its
  // approvals, its stop) by the client it started on.
  const clientsRef = useRef(
    new Map<string, { local: LocalWorkspaceServer | null; client: CrowdexAgentClient }>(),
  );
  const localWorkspaceServer: LocalWorkspaceServer | null =
    localServer?.state === 'ready' ? localServer.server : null;
  const clientFor = useCallback(
    (agent: NativeAgent, workspaceRoot: string, onCloud: boolean): CrowdexAgentClient => {
      const local = onCloud ? null : localWorkspaceServer;
      const key = JSON.stringify([agent, workspaceRoot, onCloud]);
      const kept = clientsRef.current.get(key);
      if (kept !== undefined && kept.local === local) return kept.client;
      const client = new CrowdexAgentClient(agent, onCloud, workspaceRoot, local);
      clientsRef.current.set(key, { local, client });
      return client;
    },
    [localWorkspaceServer],
  );
  const agentClient = useMemo(
    () =>
      options.agent === null || options.workspaceRoot === null
        ? null
        : clientFor(options.agent, options.workspaceRoot, cloud),
    [clientFor, cloud, options.agent, options.workspaceRoot],
  );

  const preparedSessionRef = useRef<{
    client: CrowdexAgentClient;
    conversationId: string;
    signature: string;
  } | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies(modelListAttempt): a retry of the model list (it clears preparedSessionRef) prepares the session again.
  useEffect(() => {
    if (cloud || !ready || agentClient === null) return;
    let sessionOptions: CrowdexAgentSessionOptions;
    try {
      sessionOptions = currentSessionOptions();
    } catch {
      return;
    }
    const signature = JSON.stringify(sessionOptions);
    const conversationId = options.conversationId;
    const previous = preparedSessionRef.current;
    if (
      previous?.client === agentClient &&
      previous.conversationId === conversationId &&
      previous.signature === signature
    )
      return;
    // Allow quick navigation/settings changes to settle before spawning ACP.
    const timer = setTimeout(() => {
      const attempt = { client: agentClient, conversationId, signature };
      preparedSessionRef.current = attempt;
      let answered = false;
      const answer = (failure: string | null) => {
        if (answered) return;
        answered = true;
        if (preparedSessionRef.current === attempt || preparedSessionRef.current === null)
          setModelListAnswer({ conversationId, failure });
      };
      // A preparation that never answers is a failure the user can retry, not a list of none.
      const late = setTimeout(
        () => answer("The agent's models did not load in time."),
        MODEL_LIST_TIMEOUT_MS,
      );
      // A session that takes a while may be waiting for its agent's first install: say so.
      const polling = new AbortController();
      const poll = async () => {
        for (let wait = 1_000; !polling.signal.aborted; wait = 2_000) {
          await new Promise((resolve) => setTimeout(resolve, wait));
          if (polling.signal.aborted) return;
          const installing = await agentClient.installing(polling.signal).catch(() => false);
          if (polling.signal.aborted || preparedSessionRef.current !== attempt) return;
          setInstallingConversation((current) =>
            installing ? conversationId : current === conversationId ? null : current,
          );
        }
      };
      void poll();
      void agentClient
        .prepareSession(conversationId, sessionOptions)
        .then(({ sessionConfigOptions, sessionConfigError }) => {
          if (preparedSessionRef.current !== attempt) return;
          if (sessionConfigOptions === null) {
            // Unknown: the next try asks again.
            preparedSessionRef.current = null;
            answer(sessionConfigError ?? "The agent's models could not be read.");
            return;
          }
          // The agent's models before the first message (Claude Code, Codex, OpenCode, ACP).
          if (sessionConfigOptions.length > 0) setReportedConfigOptions(sessionConfigOptions);
          answer(null);
        })
        .catch((reason: unknown) => {
          if (preparedSessionRef.current === attempt) preparedSessionRef.current = null;
          // Its first line: the reason, not the agent's log.
          const message = (reason instanceof Error ? reason.message : '').split('\n')[0]?.trim();
          answer(message ? message : "The agent's models could not be read.");
        })
        .finally(() => {
          clearTimeout(late);
          polling.abort();
          setInstallingConversation((current) => (current === conversationId ? null : current));
        });
    }, 300);
    // Once started, preparation belongs to the server. Aborting on a render or
    // navigation could cancel startup while a submitted message is joining it.
    return () => clearTimeout(timer);
  }, [
    agentClient,
    cloud,
    ready,
    options.conversationId,
    currentSessionOptions,
    setReportedConfigOptions,
    modelListAttempt,
  ]);

  // A failed start is tried again a few times on its own (an install that lost the network),
  // as the popover's Retry does; each conversation and agent has its own retries.
  const failedModelList =
    modelListAnswer?.conversationId === options.conversationId && modelListAnswer.failure !== null;
  const retriesKey = `${options.conversationId}\0${activeAgentId ?? ''}`;
  const [automaticRetries, setAutomaticRetries] = useState({ key: retriesKey, count: 0 });
  const retries = automaticRetries.key === retriesKey ? automaticRetries.count : 0;
  useEffect(() => {
    if (!failedModelList || retries >= PREPARATION_RETRIES_MS.length) return;
    const timer = setTimeout(() => {
      preparedSessionRef.current = null;
      setModelListAnswer(null);
      setAutomaticRetries({ key: retriesKey, count: retries + 1 });
      setModelListAttempt((attempt) => attempt + 1);
    }, PREPARATION_RETRIES_MS[retries]);
    return () => clearTimeout(timer);
  }, [failedModelList, retries, retriesKey]);

  const executeClientTool = useCallback(
    async (name: string, input: Record<string, unknown>): Promise<unknown> => {
      if (!isCrowdexInternalToolName(name)) {
        throw new Error(`Unknown Crowdex client tool: ${name}.`);
      }
      const resolveReference = async (kind: CrowdexReferenceKind, id: string) => {
        const resolver = referenceResolverRef.current;
        if (resolver === undefined) throw new Error(`Unknown Crowdex ${kind} reference: ${id}.`);
        const result = await resolver(kind, id);
        if (result === undefined) throw new Error(`Unknown Crowdex ${kind} reference: ${id}.`);
        return result;
      };
      if (name === 'crowdex_read_chat_transcript') {
        return await resolveReference('chat', String(input['chatId'] ?? ''));
      }
      if (name === 'crowdex_read_terminal_output') {
        return await resolveReference('terminal', String(input['terminalId'] ?? ''));
      }
      if (name === 'crowdex_describe_reference') {
        const kind = crowdexReferenceKindSchema.safeParse(input['kind']);
        if (!kind.success) throw new Error('Invalid Crowdex reference kind.');
        return await resolveReference(kind.data, String(input['id'] ?? ''));
      }
      throw new Error(`Unsupported Crowdex client tool: ${name}.`);
    },
    [],
  );

  // One connection per conversation, kept while its agent and folder are: a conversation's chat
  // keeps its transport across switches, and never shares another's.
  const connectionsRef = useRef(
    new Map<string, { key: readonly unknown[]; connection: ConversationConnection }>(),
  );
  const persisted = options.persistenceTitle !== undefined;
  const serverPersisted = options.serverPersisted ?? false;
  const connectionOf = useCallback(
    (
      conversationId: string,
      client: CrowdexAgentClient | null,
      workspaceRoot: string | null,
      onCloud: boolean,
    ): ConversationConnection => {
      const key = [client, serverPersisted, persisted, onCloud, workspaceRoot] as const;
      const kept = connectionsRef.current.get(conversationId);
      if (kept?.key.every((value, index) => value === key[index])) return kept.connection;
      const contextOf = () => {
        const context = turnContextsRef.current.get(conversationId);
        if (context === undefined) throw new Error('The conversation is not ready.');
        return context;
      };
      const transport =
        client === null
          ? createUnavailableCrowdexChatTransport(serverPersisted)
          : (client.createChatTransport({
              conversationId,
              // A turn runs the model the chip shows: while the agent's list loads, it waits for
              // it, as long as its conversation is shown (one left keeps the model it had).
              sessionOptions: async () => {
                for (
                  let waited = 0;
                  contextOf().modelLoading && shownConversationRef.current === conversationId;
                  waited += 100
                ) {
                  if (waited >= MODEL_LIST_WAIT_MS)
                    throw new Error(
                      "The agent's models did not load: choose a model and send again.",
                    );
                  await new Promise((resolve) => setTimeout(resolve, 100));
                }
                return turnSessionOptions(conversationId, contextOf());
              },
              ...(!persisted || workspaceRoot === null
                ? {}
                : {
                    persistence: () => {
                      const { persistence } = contextOf();
                      return {
                        title: persistence.title,
                        collaboration: persistence.collaboration,
                        workspaceRoot,
                        agentName: persistence.agentName,
                        ...(onCloud || persistence.workspace === null
                          ? {}
                          : {
                              workspaceName: persistence.workspace.name,
                              runtime: persistence.workspace.runtime,
                            }),
                      };
                    },
                  }),
            }) ?? createUnavailableCrowdexChatTransport(serverPersisted));
      const connection: ConversationConnection = {
        conversationId,
        transport,
        executeClientTool,
        respondToInteraction: async (interaction) => {
          if (!(await client?.interact(conversationId, interaction))) {
            throw new Error('The active agent interaction is no longer available.');
          }
        },
        cancelGeneration: async () => {
          await client?.cancel(conversationId);
        },
        predictNextMessage: async (abortSignal) => {
          const context = turnContextsRef.current.get(conversationId);
          if (client === null || context === undefined || context.unavailable !== null) return null;
          return predictionFromOutput(
            await client.generateInConversation(
              conversationId,
              MESSAGE_PREDICTION_PROMPT,
              readOnly(turnSessionOptions(conversationId, context)),
              abortSignal,
            ),
          );
        },
      };
      connectionsRef.current.set(conversationId, { key, connection });
      return connection;
    },
    [executeClientTool, persisted, serverPersisted],
  );
  const conversationConnection = useMemo(
    () => connectionOf(options.conversationId, agentClient, options.workspaceRoot, cloud),
    [agentClient, cloud, connectionOf, options.conversationId, options.workspaceRoot],
  );

  /**
   * The connection of a conversation that is not shown, where it runs now (its worktree made, its
   * sandbox started since it was): its turn runs on the server, with what it had when it was
   * last shown. `null` while it cannot run one (never shown here, its agent unavailable then, or
   * this machine's server not ready).
   */
  const connectionFor = useCallback(
    (conversationId: string, place: ConversationRunPlace): ConversationConnection | null => {
      const context = turnContextsRef.current.get(conversationId);
      if (context === undefined || context.unavailable !== null) return null;
      if (!place.cloud && localWorkspaceServer === null) return null;
      turnContextsRef.current.set(conversationId, {
        ...context,
        persistence: { ...context.persistence, workspace: place.persistenceWorkspace },
      });
      return connectionOf(
        conversationId,
        clientFor(place.agent, place.workspaceRoot, place.cloud),
        place.workspaceRoot,
        place.cloud,
      );
    },
    [clientFor, connectionOf, localWorkspaceServer],
  );

  // Another agent has other options: the ones chosen for this one are over.
  const previousAgentIdRef = useRef(activeAgentId);
  useEffect(() => {
    const previous = previousAgentIdRef.current;
    previousAgentIdRef.current = activeAgentId;
    setReportedConfigState({ agentId: activeAgentId, options: [] });
    if (previous !== null && activeAgentId !== null && previous !== activeAgentId)
      chooseSettings({ sessionConfigValues: {} });
  }, [activeAgentId, chooseSettings]);

  const setConfigOption = useCallback(
    async (configId: string, value: string) => {
      if (configId === 'crowdex-model' && value === MODEL_LIST_RETRY) {
        // Ask the agent again: the list shows as loading until it answers.
        preparedSessionRef.current = null;
        setModelListAnswer(null);
        setModelListAttempt((attempt) => attempt + 1);
        return;
      }
      if (configId === 'model' || configId === 'crowdex-model') {
        setModelId(value);
        // The conversation runs it from now on, its server-started turns too, and the next
        // conversation with this agent (on any device) starts on it.
        void saveConversationModel(options.conversationId, value);
        if (preferenceAgentId !== null) {
          setPreference((current) => ({
            agentId: preferenceAgentId,
            value: {
              listed: current?.agentId === preferenceAgentId ? current.value.listed : [],
              model: value,
            },
          }));
          void saveAgentModelPreference(preferenceAgentId, { model: value });
        }
        return;
      }
      if (
        configId === PERMISSION_CONFIG_ID &&
        (value === 'allow-reads' || value === 'allow-edits' || value === 'allow-all')
      ) {
        chooseSettings({ permissionMode: value });
        return;
      }
      const option = (
        reportedConfigOptions.length > 0
          ? reportedConfigOptions
          : nativeAgentConfigOptions(options.agent, sessionConfigValues)
      ).find((candidate) => candidate.id === configId);
      if (option?.type !== 'select') return;
      chooseSettings({ sessionConfigValues: { ...sessionConfigValues, [configId]: value } });
      if (option.category === 'model') setModelId(value);
    },
    [
      chooseSettings,
      options.agent,
      options.conversationId,
      preferenceAgentId,
      reportedConfigOptions,
      sessionConfigValues,
      setModelId,
    ],
  );
  const agentMode = useMemo(() => agentModeChoices(reportedConfigOptions), [reportedConfigOptions]);
  const setPlanFirst = useCallback(
    (enabled: boolean) => {
      if (agentMode === null || agentMode.plan === null) return;
      const id = agentMode.option.id;
      const values = { ...sessionConfigValues };
      if (enabled) values[id] = agentMode.plan as string;
      else delete values[id];
      chooseSettings({ sessionConfigValues: values });
    },
    [agentMode, chooseSettings, sessionConfigValues],
  );
  // The agent leaves its plan mode itself once its plan is accepted: Plan first is then done.
  const reportedAgentMode = agentMode?.option.currentValue;
  const previousAgentModeRef = useRef(reportedAgentMode);
  useEffect(() => {
    const previous = previousAgentModeRef.current;
    previousAgentModeRef.current = reportedAgentMode;
    if (agentMode?.plan == null || previous !== agentMode.plan) return;
    if (reportedAgentMode !== agentMode.plan) setPlanFirst(false);
  }, [agentMode?.plan, reportedAgentMode, setPlanFirst]);
  const steerGeneration = useCallback(
    async (text: string, messageId: string, answers?: string): Promise<boolean> => {
      if (options.agent === null || !ready || text.trim().length === 0) return false;
      return (await agentClient?.steer(options.conversationId, text, messageId, answers)) ?? false;
    },
    [agentClient, options.agent, options.conversationId, ready],
  );

  // A local session says its models once readied; a cloud one with its first answer.
  const answeredModels =
    modelListAnswer?.conversationId === options.conversationId ? modelListAnswer : null;
  const discoveringModels =
    options.agent !== null &&
    !cloud &&
    reportedModelOption === undefined &&
    answeredModels === null &&
    error === null;
  turnContextsRef.current.set(options.conversationId, {
    ...shownSession,
    modelLoading: discoveringModels && selectedModelId === '',
    persistence: {
      title: options.persistenceTitle ?? 'New conversation',
      collaboration,
      agentName: aiAgent?.name ?? options.agent?.name ?? options.agent?.id ?? 'Crowdex Agent',
      workspace: options.persistenceWorkspace ?? null,
    },
  });
  const modelListFailure =
    reportedModelOption === undefined && !cloud ? (answeredModels?.failure ?? null) : null;
  const agentName = options.agent?.name ?? 'The agent';
  // A model that ran (an older conversation's) but that the list does not name still shows.
  const shownModelChoices =
    selectedModelId === '' || matchModelChoice(selectedModelId, modelChoices) !== null
      ? modelChoices
      : [{ value: selectedModelId, name: modelDisplayName(selectedModelId) }, ...modelChoices];
  const installing = installingConversation === options.conversationId;
  const configOptions: readonly AcpSessionConfigOption[] = [
    {
      id: 'crowdex-model',
      name: 'Model',
      category: 'model',
      type: 'select',
      currentValue: selectedModelId,
      // No model to show yet: the chip and the popover say why. "Listed no models" only for an
      // agent that answered with an empty list; a failed or slow answer offers Retry.
      // The agent's own note on its models stays ("connect a provider in OpenCode to add more").
      ...(shownModelChoices.length > 0 || options.agent === null
        ? reportedModelOption?.description
          ? { description: reportedModelOption.description }
          : {}
        : modelListStatus(
            installing
              ? { state: 'loading', message: `Installing ${agentName}…` }
              : discoveringModels
                ? { state: 'loading' }
                : modelListFailure !== null
                  ? { state: 'failed', message: modelListFailure }
                  : cloud && reportedModelOption === undefined
                    ? {
                        state: 'empty',
                        message: `${agentName}'s models show after its first answer`,
                      }
                    : { state: 'empty', message: `${agentName} listed no models` },
          )),
      options: shownModelChoices,
    },
    // The agent's own permission mode is not offered: Crowdex's permissions are the only one.
    // What the agent reports wins; the known defaults (effort) fill in what it does not report.
    ...[
      ...reportedConfigOptions,
      ...nativeAgentConfigOptions(options.agent, sessionConfigValues).filter(
        (option) =>
          !reportedConfigOptions.some((reported) => reported.category === option.category),
      ),
    ].filter((option) => option.category !== 'model' && option.category !== 'mode'),
    {
      id: PERMISSION_CONFIG_ID,
      name: 'Permissions',
      category: 'permission',
      type: 'select',
      currentValue: runtimePermissionMode(
        conversationSettings.permissionMode,
        aiAgent?.policy?.permissionMode,
      ),
      options: [
        { value: 'allow-reads', name: 'Ask', description: 'Ask before edits and commands.' },
        { value: 'allow-edits', name: 'Auto edits', description: 'Allow edits; ask for commands.' },
        {
          value: 'allow-all',
          name: 'Full access',
          description: 'Run every agent action without asking or reviewing it.',
        },
      ],
    },
  ];
  const planFirst =
    agentMode === null || agentMode.plan === null
      ? null
      : {
          enabled: sessionConfigValues[agentMode.option.id] === agentMode.plan,
          set: (enabled: boolean) => setPlanFirst(enabled),
        };
  const conversationId = ready ? options.conversationId : null;

  return {
    status,
    startupPhase,
    conversationId,
    conversationWorkspaceRoot: conversationId === null ? null : options.workspaceRoot,
    agentTitle: conversationId === null ? null : (options.agent?.name ?? null),
    /** The shown conversation's connection (see `ConversationConnection`). */
    conversationConnection,
    connectionFor,
    transport: conversationConnection.transport,
    executeClientTool,
    respondToInteraction: conversationConnection.respondToInteraction,
    cancelGeneration: conversationConnection.cancelGeneration,
    steerGeneration,
    diagnostics: [],
    modelId: selectedModelId || null,
    configOptions,
    setReportedConfigOptions,
    /** The model the latest reply ran (its metadata's `modelId`), from the transcript. */
    setReplyModelId,
    collaboration,
    /** The agent's plan mode, when it offers one: it proposes a plan before acting. */
    planFirst,
    pluginWorkbench,
    availableSkills: pluginSkills.availableSkills,
    setConfigOption,
    generateGitText: async (
      prompt: string,
      outputSchema: Readonly<Record<string, unknown>>,
    ): Promise<unknown> => {
      if (options.agent === null || !ready) throw new Error('The selected agent is not ready.');
      if (agentClient === null) throw new Error('The selected agent is not ready.');
      return await agentClient.generateObject(
        options.conversationId,
        `Generate Git metadata only. Do not use tools or modify files. Return only the requested JSON object.\n\n${prompt}`,
        outputSchema,
        readOnly(currentSessionOptions()),
      );
    },
    /** `@persona` (design D51): the saved agent runs on this conversation's agent, and takes its turn. */
    personaRunsHere: async (profileId: string): Promise<boolean> => {
      const persona = (await readAgentPersonas()).find((entry) => entry.id === profileId);
      return (
        persona !== undefined &&
        options.agent !== null &&
        personaSharesRuntime(persona.agentId, options.agent.id)
      );
    },
    /** `@persona` on another runtime: its read-only reply, generated aside (design D51). */
    askPersona: async (
      profileId: string,
      message: { id: string; text: string },
    ): Promise<CrowdexUIMessage[]> => {
      if (agentClient === null) throw new Error('The selected agent is not ready.');
      return await agentClient.personaReply(
        options.conversationId,
        profileId,
        message,
        currentSessionOptions(),
      );
    },
    /** The user's likely next message after the latest reply, or null when there is none. */
    predictNextMessage: conversationConnection.predictNextMessage,
  };
}

/** The models an agent last listed on the account, as a model option (none when it listed none). */
export function accountModelOption(listed: readonly string[]): AcpSelectConfigOption | undefined {
  if (listed.length === 0) return undefined;
  return {
    id: 'model',
    name: 'Model',
    category: 'model',
    type: 'select',
    currentValue: listed[0] ?? '',
    options: listed.map((value) => ({ value, name: modelDisplayName(value) })),
  };
}
